import React from 'react';
import { PropsWithChildren, createContext, useContext, useState, Dispatch, SetStateAction, useEffect } from "react";
import { EditorState, LayoutState, ThemeState, CSSState } from "../types/state.types";
import { Group } from '../types/group.types';
import { Container, Container as ContainerType } from '../types/canvas.types';
import { useComponentRegistry } from './ComponentRegistryContext';
import {CustomCSSModel} from "../components/CustomCSS";
import {UIModelAPI} from "../types/api.types";
import { generateInstanceId } from '../utils/IdGenerator';
import { containerStore } from '../utils/containerRegistryBridge';
import {UIModelTheme} from "../components/ThemeEditor";

type EditorContextType = {
    editorState: EditorState;
    setEditorState: Dispatch<SetStateAction<EditorState>>;
    layoutState: LayoutState;
    setLayoutState: Dispatch<SetStateAction<LayoutState>>;
    themeState: ThemeState;
    setThemeState: Dispatch<SetStateAction<ThemeState>>;
    cssState: CSSState;
    setCssState: Dispatch<SetStateAction<CSSState>>;
    groups: Group[];
    setGroups: Dispatch<SetStateAction<Group[]>>;
    containers: { [pageId: string]: ContainerType[] };
    setContainers: Dispatch<SetStateAction<{ [pageId: string]: ContainerType[] }>>;
    addComponentToContainer: (containerId: string, componentId: string, componentType: string) => void;
    removeComponentFromContainer: (componentId: string, containerId: string) => void;
    addComponentsBatch: (containerId: string, components: { id: string, type: string }[]) => void;
    selectedFieldId: string | null;
    selectedFieldType: string | null;
    selectedFieldParentId: string | null;
    selectedWidgetId: string | null;
    selectedWidgetType: string | null;
    selectField: (fieldId: string, fieldType: string, parentId?: string, containerId?: string) => void;
    selectSubmodel: (submodelId: string, containerId?: string) => void;
    selectWidget: (widgetId: string, widgetType: string, configuration?: any, containerId?: string) => void;
    selectContainer: (containerId: string, path?: string[]) => void;
    clearAllSelections: () => void;
    setSelectionPath: (path: string[]) => void;
    clearSelectionPath: () => void;
    uiModel: UIModelAPI | null;
    setUiModel: Dispatch<SetStateAction<UIModelAPI | null>>;
    customCSS: CustomCSSModel | null;
    setCustomCSS: Dispatch<SetStateAction<CustomCSSModel | null>>;
    updateWidgetContent: (widgetId: string, content: string) => void
    deleteContainer: (containerId: string) => void
    theme: UIModelTheme;
    setTheme: Dispatch<SetStateAction<UIModelTheme>>;
    moveWidgetBetweenContainers: (widgetId: string, widgetType: string, fromContainerId: string, toContainerId: string, config?: any) => void;
    saveWidgetConfig: (widgetId: string, config: any) => void;
    getWidgetConfig: (widgetId: string) => any;
}

const initialEditorState: EditorState = {
    activeGroupId: null,
    activePageId: null,
    selectedContainerId: null,
    selectedFieldId: null,
    selectedFieldType: null,
    selectedFieldParentId: null,
    selectedSubmodelId: null,
    selectedWidgetId: null,
    selectedWidgetType: null,
    componentUsage: {},
    componentRegistry: {
        subModels: {},
        widgets: {}
    },
    fieldConfigurations: {},
    selectionPath: [],
};

const initialLayoutState: LayoutState = {
    activeComponentId: null,
    components: [],
    containers: [],
    selectedElements: []
};

const initialThemeState: ThemeState = {
    colors: {
        primary: '#1a73e8',
        secondary: '#f1f3f4',
        background: '#ffffff',
        text: '#202124'
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        fontSize: {
            base: '14px',
            heading: '24px',
            subheading: '18px'
        }
    },
    spacing: {
        unit: '8px',
        container: '16px',
        element: '12px'
    }
};

const initialCSSState: CSSState = {
    customStyles: {},
    globalStyles: ''
};

const initialGroups: Group[] = [];

const initialContainers: { [pageId: string]: ContainerType[] } = {};

export const EditorContext = createContext<EditorContextType | undefined>(undefined);

export const EditorProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { getComponentDefinition, releaseComponent, saveWidgetConfig: saveWidgetConfigToRegistry, getWidgetConfig: getWidgetConfigFromRegistryMethod, clearWidgetConfig: clearWidgetConfigFromRegistry, moveWidget, canAddYourBuildToContainer, canUseComponent } = useComponentRegistry();
    const [editorState, setEditorState] = useState<EditorState>(initialEditorState);
    const [layoutState, setLayoutState] = useState<LayoutState>(initialLayoutState);
    const [themeState, setThemeState] = useState<ThemeState>(initialThemeState);
    const [cssState, setCssState] = useState<CSSState>(initialCSSState);
    const [groups, setGroups] = useState<Group[]>(initialGroups);
    const [uiModel, setUiModel] = useState<UIModelAPI | null>(null);
    const [customCSS, setCustomCSS] = useState<CustomCSSModel | null>(null);
    const [theme, setTheme] = useState<UIModelTheme>(
        {
            solidButtonBackground: "#0747A6",
            solidButtonLabel: "#ffffff",
            borderButtonBorder: "#0747A6",
            borderButtonLabel: "#0747A6",
            textButtonLabel: "#0747A6",
            titleColor: "#0747A6",
            subtitleColor: "#172B4D",
            bodyTextColor: "#6D788C",
            mainBackgroundColor: "#ffffff",
            headerBackgroundColor: "#ffffff",
            navigationBackgroundColor: "#ffffff"
        }
    );
    const [containers, setContainers] = useState<{ [pageId: string]: ContainerType[] }>(initialContainers);

    const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
    const [selectedFieldType, setSelectedFieldType] = useState<string | null>(null);
    const [selectedFieldParentId, setSelectedFieldParentId] = useState<string | null>(null);
    const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
    const [selectedWidgetType, setSelectedWidgetType] = useState<string | null>(null);

    const getWidgetType = (componentId: string): string => {
        if (componentId.includes('content-block')) return 'contentBlock';
        if (componentId.includes('divider')) return 'divider';
        if (componentId.includes('your-build')) return 'yourBuild';
        if (componentId.includes('summary')) return 'summary';
        if (componentId.includes('visualizer')) return 'visualizer';
        if (componentId.includes('button')) return 'button';
        return 'contentBlock';
    };

    const clearSelectionPath = () => {
        setEditorState(prev => ({
            ...prev,
            selectionPath: []
        }));
    };

    const isReusableComponent = (componentType: string, componentId: string): boolean => {
        if (componentType === 'widget') {
            if (componentId.includes('divider') || componentId.includes('content-block')) {
                return true;
            }
            return false;
        }
        return false;
    };

    const selectComponent = (componentId: string, componentType: string, containerId?: string, parentId?: string) => {
        const path: string[] = [];

        if (editorState.activePageId && containers[editorState.activePageId]) {
            const pageContainers = containers[editorState.activePageId];

            const findComponentPath = (containerList: ContainerType[], targetId: string, currentPath: string[] = []): string[] | null => {
                for (const container of containerList) {
                    const containerPath = [...currentPath, container.id];

                    const hasComponent = container.content.items?.some(item =>
                        item.id === targetId &&
                        (item.type === 'field' || item.type === 'category' || item.type === 'connector' || item.type === 'widget')
                    );

                    if (hasComponent) {
                        if (container.content.submodel) {
                            return [...containerPath, container.content.submodel.id, targetId];
                        }
                        return [...containerPath, targetId];
                    }

                    if (container?.content?.nestedContainers && container?.content?.nestedContainers?.length > 0) {
                        const nestedPath = findComponentPath(container.content.nestedContainers, targetId, containerPath);
                        if (nestedPath) return nestedPath;
                    }
                }
                return null;
            };

            if (containerId) {
                const findContainerAndBuildPath = (containerList: ContainerType[], targetContainerId: string, currentPath: string[] = []): string[] | null => {
                    for (const container of containerList) {
                        const containerPath = [...currentPath, container.id];

                        if (container.id === targetContainerId) {
                            if (container.content.submodel) {
                                return [...containerPath, container.content.submodel.id, componentId];
                            }
                            return [...containerPath, componentId];
                        }

                        if (container?.content?.nestedContainers && container?.content?.nestedContainers?.length > 0) {
                            const nestedPath = findContainerAndBuildPath(container.content.nestedContainers, targetContainerId, containerPath);
                            if (nestedPath) return nestedPath;
                        }
                    }
                    return null;
                };

                const containerPath = findContainerAndBuildPath(pageContainers, containerId);
                if (containerPath) {
                    path.push(...containerPath);
                }
            } else {
                const componentPath = findComponentPath(pageContainers, componentId);
                if (componentPath) {
                    path.push(...componentPath);
                }
            }
        }

        setSelectedFieldId(null);
        setSelectedFieldType(null);
        setSelectedFieldParentId(null);
        setSelectedWidgetId(null);
        setSelectedWidgetType(null);

        if (componentType === 'field') {
            setSelectedFieldId(componentId);
            setSelectedFieldType('field');
            setSelectedFieldParentId(parentId || null);

            setEditorState(prev => ({
                ...prev,
                selectedFieldId: componentId,
                selectedFieldType: 'field',
                selectedFieldParentId: parentId || null,
                selectedSubmodelId: null,
                selectedContainerId: containerId || null,  // Preserve container context when field is selected
                selectedWidgetId: null,
                selectedWidgetType: null,
                selectionPath: path
            }));
        } else if (componentType === 'category') {
            setSelectedFieldId(componentId);
            setSelectedFieldType('category');
            setSelectedFieldParentId(parentId || null);

            setEditorState(prev => ({
                ...prev,
                selectedFieldId: componentId,
                selectedFieldType: 'category',
                selectedFieldParentId: parentId || null,
                selectedSubmodelId: null,
                selectedContainerId: containerId || null,  // Preserve container context when category is selected
                selectedWidgetId: null,
                selectedWidgetType: null,
                selectionPath: path
            }));
        } else if (componentType === 'connector') {
            setSelectedFieldId(componentId);
            setSelectedFieldType('connector');
            setSelectedFieldParentId(parentId || null);

            setEditorState(prev => ({
                ...prev,
                selectedFieldId: componentId,
                selectedFieldType: 'connector',
                selectedFieldParentId: parentId || null,
                selectedSubmodelId: null,
                selectedContainerId: containerId || null,  // Preserve container context when connector is selected
                selectedWidgetId: null,
                selectedWidgetType: null,
                selectionPath: path
            }));
        } else if (componentType === 'widget') {
            setEditorState(prev => ({
                ...prev,
                selectedContainerId: containerId || null,  // Preserve container context when widget is selected
                selectedFieldId: null,
                selectedFieldType: null,
                selectedFieldParentId: null,
                selectedSubmodelId: null,
                selectedWidgetId: null,
                selectedWidgetType: null,
                selectionPath: path
            }));
        }
    };

        const selectField = (fieldId: string, fieldType: string, parentId?: string, containerId?: string) => {
        const componentDef = getComponentDefinition(fieldId);
        let actualType = 'field';

        if (componentDef) {
            if (componentDef.type === 'category') actualType = 'category';
            else if (componentDef.type === 'connector') actualType = 'connector';
            else if (fieldType === 'category') actualType = 'category';
            else if (fieldType === 'connector') actualType = 'connector';
        }

        selectComponent(fieldId, actualType, containerId, parentId);
    };

    const selectWidget = (widgetId: string, widgetType: string, configuration?: any, containerId?: string) => {
        setSelectedFieldId(null);
        setSelectedFieldType(null);
        setSelectedFieldParentId(null);

        setSelectedWidgetId(widgetId);
        setSelectedWidgetType(widgetType);

        setEditorState(prev => ({
            ...prev,
            selectedWidgetId: widgetId,
            selectedWidgetType: widgetType,
            selectedFieldId: null,
            selectedFieldType: null,
            selectedFieldParentId: null,
            selectedSubmodelId: null,
            selectedContainerId: containerId || prev.selectedContainerId  // Preserve container context when widget is selected
        }));
    };

    const selectSubmodel = (submodelId: string, containerId?: string) => {
        const path: string[] = [];

        if (editorState.activePageId && containers[editorState.activePageId]) {
            const pageContainers = containers[editorState.activePageId];


            const findSubmodelPath = (containerList: ContainerType[], targetSubmodelId: string, currentPath: string[] = []): string[] | null => {
                for (const container of containerList) {
                    const containerPath = [...currentPath, container.id];

                    if (container.content.submodel?.id === targetSubmodelId) {
                        return [...containerPath, targetSubmodelId];
                    }

                    if (container?.content?.nestedContainers && container?.content?.nestedContainers?.length > 0) {
                        const nestedPath = findSubmodelPath(container.content.nestedContainers, targetSubmodelId, containerPath);
                        if (nestedPath) return nestedPath;
                    }
                }
                return null;
            };

            const submodelPath = findSubmodelPath(pageContainers, submodelId);
            if (submodelPath) {
                path.push(...submodelPath);
            }
        }

        setSelectedFieldId(null);
        setSelectedFieldType(null);
        setSelectedFieldParentId(null);
        setSelectedWidgetId(null);
        setSelectedWidgetType(null);

        setEditorState(prev => ({
            ...prev,
            selectedSubmodelId: submodelId,
            selectedFieldId: null,
            selectedFieldType: null,
            selectedFieldParentId: null,
            selectedWidgetId: null,
            selectedWidgetType: null,
            selectedContainerId: containerId || prev.selectedContainerId,  // Preserve container context when submodel is selected
            selectionPath: path
        }));
    };

    useEffect(() => {
        if (groups.length > 0 && (!editorState.activeGroupId || !editorState.activePageId)) {
            const firstGroup = groups[0];
            const firstTab = firstGroup.pages[0];

            setEditorState(prev => ({
                ...prev,
                activeGroupId: firstGroup.id,
                activePageId: firstTab.id,
                selectionPath: []
            }));
        }
    }, [groups, editorState.activeGroupId, editorState.activePageId]);

    const setSelectionPath = (path: string[]) => {
        setEditorState(prev => ({
            ...prev,
            selectionPath: path
        }));
    };

    const clearAllSelections = () => {
        setSelectedFieldId(null);
        setSelectedFieldType(null);
        setSelectedFieldParentId(null);
        setSelectedWidgetId(null);
        setSelectedWidgetType(null);

        setEditorState(prev => ({
            ...prev,
            selectedFieldId: null,
            selectedFieldType: null,
            selectedFieldParentId: null,
            selectedWidgetId: null,
            selectedWidgetType: null,
            selectedSubmodelId: null,
            selectedContainerId: null,
            selectionPath: []
        }));
    };

    const selectContainer = (containerId: string, path: string[] = []) => {
        setSelectedFieldId(null);
        setSelectedFieldType(null);
        setSelectedFieldParentId(null);
        setSelectedWidgetId(null);
        setSelectedWidgetType(null);
        
        setEditorState(prev => ({
            ...prev,
            selectedContainerId: containerId,
            selectedFieldId: null,
            selectedFieldType: null,
            selectedFieldParentId: null,
            selectedWidgetId: null,
            selectedWidgetType: null,
            selectedSubmodelId: null,
            selectionPath: path
        }));
    };

    const getFieldType = (componentId: string): string => {

        const fieldDef = getComponentDefinition(componentId);

        if (!fieldDef) {
            console.warn(`Field definition not found for ID: ${componentId}`);
            return 'text';
        }

        if (fieldDef.type === 'category') {
            return 'category';
        }

        if (fieldDef.type === 'connector') {
            return 'connector';
        }

        if (fieldDef.fieldType) {
            const fieldTypeLower = fieldDef?.fieldType?.toLowerCase();
            
            if (fieldTypeLower === 'multi-choice' || fieldTypeLower === 'multichoice') {
                return 'multichoice';
            }
            if (fieldTypeLower === 'integer') {
                return 'integer';
            }
            if (fieldTypeLower === 'decimal') {
                return 'decimal';
            }
            if (fieldTypeLower === 'fraction') {
                return 'fraction';
            }
            if (fieldTypeLower === 'boolean') {
                return 'boolean';
            }
            if (fieldTypeLower === 'range') {
                return 'range';
            }
            if (fieldTypeLower === 'text') {
                return 'text';
            }
        }

        const fieldName = fieldDef.name?.toLowerCase() || '';
        const fieldIdLower = componentId.toLowerCase();

        if ((fieldName.includes('multi') && fieldName.includes('choice')) || 
            fieldName.includes('multichoice') || 
            fieldIdLower.includes('multichoice')) {
            return 'multichoice';
        }

        if (fieldName.includes('integer') || fieldIdLower.includes('integer')) {
            return 'integer';
        }

        if (fieldName.includes('decimal') || fieldIdLower.includes('decimal')) {
            return 'decimal';
        }

        if (fieldName.includes('fraction') || fieldIdLower.includes('fraction')) {
            return 'fraction';
        }

        if (fieldName.includes('boolean') || fieldName.includes('bool') ||
            fieldIdLower.includes('boolean') || fieldIdLower.includes('bool')) {
            return 'boolean';
        }

        if (fieldName.includes('range') || fieldIdLower.includes('range')) {
            return 'range';
        }

        if (fieldName.includes('text') || fieldIdLower.includes('text')) {
            return 'text';
        }

        return 'text';
    };

    const getMultiChoiceConfig = (fieldDef: any) => {
        return {
            style: 'Chip',
            chipSize: 'Medium',
            showImage: false,
            imageDisplay: 'Fit',
            layoutStyle: 'Standard',
            captionAlignment: 'Centre',
            columnSelection: 'Auto',
            fillDirection: 'Vertical',
            layoutDirection: 'Horizontal',
            showTitle: true,
            displayName: '',
            chipWidth: 'Fixed',
            chipLabelPosition: 'Below Image',
            showSearchBar: false,
            showExpandCollapse: false,
            loadOption: 'All',
            loadMoreButtonLabel: 'Load More',
            loadLessButtonLabel: 'Show Less',
            initialLoadCount: 5,
            loadRemainingOption: 'Show All',
            batchCount: 5,
            excludedOption: 'Disable'
        };
    };

    const getCategoryConfig = (categoryDef: any) => {
        const categoryName = categoryDef?.name || 'Category';

        return {
            // Category Details
            titleText: `Select ${categoryName}`,
            showCategoryDescription: true,
            descriptionText: 'Compatibility based on selections in your build',
            showThumbnail: true,
            imageDisplay: 'Fit',
            categoryImageUrl: '',
            showSortByAttributes: true,
            showInStock: true,
            inStockText: 'Show In Stock only',
            hideIfNoProducts: false,

            // Product Listing
            style: 'Grid',
            productsPerRow: '4',
            productsLoading: 'Infinite Scroll',
            scrollBarAfterRows: '4',
            showButtonAfterProducts: '20',
            scrollBarAfterProducts: '20',

            // Product Details
            showModelNumber: true,
            showProductDescription: true,
            descriptionStyle: 'Partly',
            showPrice: true,
            showAvailabilityStatus: true,
            inStockLabel: 'In Stock',
            backOrderLabel: 'Enter Label',
            showVariants: false,
            showNumberOfVariants: false,
            showCategory: false,
            showManufacturer: false,
            productDetailsTextAlignment: 'Left',
            showButtonText: true,
            buttonLabel: 'Add to Cart',
            showButtonIcon: false,
            buttonPlacement: 'Left',
            buttonIconUrl: '',

            direction: 'vertical',
            showInStockOnly: false,
            sortOptions: ['Attributes'],
            selectedSort: 'Attributes'
        };
    };

    const getDefaultFieldConfiguration = (fieldType: string, fieldName: string) => {
        const baseConfig = {
            label: fieldName,
            fieldType: fieldType,
            fieldName: fieldName,
            required: false,
            exclusionType: 'hide' as 'hide' | 'disable',
            showTitle: true,
            displayName: fieldName
        };

        switch (fieldType) {
            case 'text':
                return {
                    ...baseConfig,
                    value: ''
                };

            case 'integer':
                return {
                    ...baseConfig,
                    value: 0
                };

            case 'decimal':
                return {
                    ...baseConfig,
                    value: 0.0,
                    decimalPlaces: 2
                };

            case 'fraction':
                return {
                    ...baseConfig,
                    numerator: 0,
                    denominator: 1
                };

            case 'boolean':
                return {
                    ...baseConfig,
                    value: false,
                    trueLabel: 'Yes',
                    falseLabel: 'No'
                };

            case 'range':
                return {
                    ...baseConfig,
                    value: 50,
                    min: 0,
                    max: 100,
                    step: 1
                };

            case 'multichoice':
                return {
                    ...baseConfig,
                    style: 'Chip',
                    chipSize: 'Medium',
                    showImage: false,
                    imageDisplay: 'Fit',
                    layoutStyle: 'Standard',
                    captionAlignment: 'Centre',
                    columnSelection: 'Auto',
                    fillDirection: 'Vertical',
                    layoutDirection: 'Horizontal',
                    chipWidth: 'Fixed',
                    chipLabelPosition: 'Below Image',
                    showSearchBar: false,
                    showExpandCollapse: false,
                    loadOption: 'All',
                    loadMoreButtonLabel: 'Load More',
                    loadLessButtonLabel: 'Show Less',
                    initialLoadCount: 5,
                    loadRemainingOption: 'Show All',
                    batchCount: 5,
                    excludedOption: 'Disable',
                };

            case 'category':
                return {
                    ...baseConfig,
                    fieldType: 'category',
                    ...getCategoryConfig({ name: fieldName })
                };

            case 'connector':
                return {
                    ...baseConfig,
                    connectorType: 'standard'
                };

            default:
                return baseConfig;
        }
    };

    const updateWidgetContent = (widgetId: string, content: string) => {
        setContainers(prevContainers => {
            const activePageId = editorState.activePageId;
            if (!activePageId || !prevContainers[activePageId]) return prevContainers;

            const updateContainerWidgets = (containers: Container[]): Container[] => {
                return containers.map(container => {
                    const updatedItems = container.content.items?.map(item => {
                        if (item.id === widgetId && item.type === 'widget' && item.widgetType === 'contentBlock') {
                            return { ...item, content };
                        }
                        return item;
                    }) || [];

                    const updatedUsedComponents = container.usageInfo.usedComponents.map(comp => {
                        if (comp.id === widgetId && comp.type === 'widget') {
                            return { ...comp, content };
                        }
                        return comp;
                    });

                    const updatedNestedContainers = container.content.nestedContainers 
                        ? updateContainerWidgets(container.content.nestedContainers)
                        : [];

                    return {
                        ...container,
                        content: {
                            ...container.content,
                            items: updatedItems,
                            nestedContainers: updatedNestedContainers
                        },
                        usageInfo: {
                            usedComponents: updatedUsedComponents
                        }
                    };
                });
            };

            return {
                ...prevContainers,
                [activePageId]: updateContainerWidgets(prevContainers[activePageId])
            };
        });
    };

    const getDefaultSubmodelConfiguration = (submodelName: string) => {
        return {
            showTitle: true,
            displayName: '',
            fieldName: submodelName,
            hideIfNoProducts: false,
            exclusionType: 'hide' as 'hide' | 'disable'
        };
    };
    
    const addComponentToContainer = (containerId: string, componentId: string, componentType: string) => {
        const activePageId = editorState.activePageId;
        if (!activePageId) return;

        if (componentType === 'widget' && componentId === 'your-build-widget') {
            if (!canUseComponent(componentId, containerId, activePageId)) {
                console.warn('Your Build widget can only be added to one container per page');
                return;
            }
        }

        setContainers(prev => {
            const pageContainers = prev[activePageId] || [];

            const updateContainersRecursively = (containers: ContainerType[]): { updatedContainers: ContainerType[], addedSubmodel: any } => {
                let addedSubmodel: any = null;
                const updatedContainers = containers.map(container => {
                    if (container.id === containerId) {
                        const componentDef = getComponentDefinition(componentId);
                        let updatedSubmodel = container.content.submodel;

                        if ((componentType === 'field' || componentType === 'category' || componentType === 'connector') && componentDef?.parentId) {
                            const parentSubmodelDef = getComponentDefinition(componentDef.parentId);
                            updatedSubmodel = {
                                id: componentDef.parentId,
                                name: parentSubmodelDef?.name || 'Unknown Submodel',
                                isTemporary: false,
                                configuration: getDefaultSubmodelConfiguration(parentSubmodelDef?.name || 'Unknown Submodel')
                            };
                            addedSubmodel = updatedSubmodel;
                        } else if (componentType === 'submodel') {
                            updatedSubmodel = {
                                id: componentId,
                                name: componentDef?.name || 'Unknown Submodel',
                                isTemporary: false,
                                configuration: getDefaultSubmodelConfiguration(componentDef?.name || 'Unknown Submodel')
                            };
                            addedSubmodel = updatedSubmodel;
                        }

                        const widgetTypeValue = componentType === 'widget' ? getWidgetType(componentId) : null;
                        const uniqueInstanceId = (componentType === 'field' || componentType === 'category' || componentType === 'connector')
                            ? componentId
                            : generateInstanceId(componentId);

                        const componentToAdd: any = {
                            id: uniqueInstanceId,
                            type: componentType,
                            originalComponentId: componentId,
                            isReusable: (componentType === 'field' || componentType === 'category' || componentType === 'connector') ? true : isReusableComponent(componentType, componentId),
                            ...(componentType === 'widget' && widgetTypeValue === 'contentBlock' && { content: '' })
                        };

                        let contentType = container.content.type;
                        let contentComponentId = container.content.componentId;
                        let widgetType = container.content['widget-type'];
                        let itemToAdd: any = null;
                        let newItems = [...(container.content.items || [])];
                        let newUsedComponents = [...container.usageInfo.usedComponents];

                        if ((componentType === 'field' || componentType === 'category' || componentType === 'connector') && componentDef) {
                            if (updatedSubmodel) {
                                contentType = 'subModel';
                                contentComponentId = updatedSubmodel.id;
                                widgetType = undefined;
                            }

                            const fieldType = getFieldType(componentId);
                            const fieldName = componentDef.name || 'Field';
                            const defaultConfig = getDefaultFieldConfiguration(fieldType, fieldName);

                            if (componentType === 'category' && componentDef?.dataConfiguration) {
                                const dataConfig = componentDef.dataConfiguration;
                                const wrapperId = dataConfig.id || componentId;
                                const categoryId = dataConfig.categoryId || dataConfig.actualCategory?.id || null;
                                const isEmpty = dataConfig.isEmpty || (!dataConfig.actualCategory && !categoryId);

                                if (isEmpty) {
                                    console.warn(`Category "${dataConfig.wrapperName}" is missing actual category data. Wrapper ID: ${wrapperId}`);
                                }

                                itemToAdd = {
                                    id: wrapperId,
                                    categoryId: categoryId,
                                    name: fieldName,
                                    type: componentType,
                                    fieldType: undefined,
                                    parentId: componentDef.parentId || updatedSubmodel?.id,
                                    isReusable: true,
                                    configuration: {
                                        ...getCategoryConfig(componentDef),
                                        ...defaultConfig,
                                        id: wrapperId,
                                        categoryId: categoryId,
                                        wrapperName: dataConfig.wrapperName,
                                        categoryName: dataConfig.actualCategoryName || dataConfig.categoryName,
                                        categoryType: dataConfig.categoryType,
                                        isEmpty: isEmpty
                                    }
                                };
                            } else {
                                itemToAdd = {
                                    id: componentId,
                                    name: fieldName,
                                    type: componentType,
                                    fieldType: componentType === 'field' ? fieldType : undefined,
                                    parentId: componentDef.parentId || updatedSubmodel?.id,
                                    isReusable: true,
                                    configuration: defaultConfig
                                };

                                if (componentType === 'field' && fieldType === 'multichoice' && componentDef?.dataConfiguration?.choices) {
                                    const firstTwoChoices = componentDef.dataConfiguration.choices.slice(0, 2);
                                    itemToAdd.dataConfiguration = { choices: firstTwoChoices };
                                    itemToAdd.configuration = {
                                        ...getMultiChoiceConfig(componentDef),
                                        ...defaultConfig,
                                        choices: firstTwoChoices
                                    };
                                }
                            }
                            newItems.push(itemToAdd);
                            newUsedComponents.push(componentToAdd);
                        } else if (componentType === 'widget') {
                            const widgetTypeValue = getWidgetType(componentId);

                            contentType = 'widget';
                            contentComponentId = uniqueInstanceId; 
                            widgetType = widgetTypeValue;

                            let widgetConfiguration: any = undefined;
                            if (widgetTypeValue === 'yourBuild') {
                                const savedGlobalConfig = getWidgetConfigFromRegistryMethod('your-build-widget');

                                if (savedGlobalConfig) {
                                    widgetConfiguration = savedGlobalConfig;
                                } else {
                                    const defaultConfig = {
                                        showTitle: true,
                                        title: 'Your Build',
                                        showShareButton: true,
                                        shareButtonConfig: {
                                            showText: true,
                                            buttonLabel: 'Share',
                                            showIcon: false
                                        },
                                        showDescription: true,
                                        description: 'Disclaimer: Compatio provides product configurations and recommendations as a guide.',
                                        showAddToCartButton: true,
                                        addToCartButtonConfig: {
                                            showText: true,
                                            buttonLabel: 'Add to Cart',
                                            showIcon: false
                                        }
                                    };
                                    widgetConfiguration = defaultConfig;
                                    saveWidgetConfigToRegistry('your-build-widget', defaultConfig);
                                }
                            } else if (widgetTypeValue === 'button') {
                                const savedGlobalConfig = getWidgetConfigFromRegistryMethod('button-widget');

                                if (savedGlobalConfig) {
                                    // Use saved config but update with current instance id
                                    widgetConfiguration = {
                                        ...savedGlobalConfig,
                                        id: uniqueInstanceId
                                    };
                                } else {
                                    const defaultConfig = {
                                        id: uniqueInstanceId,
                                        displayName: 'Button',
                                        className: '',
                                        functionName: ''
                                    };
                                    widgetConfiguration = defaultConfig;
                                    saveWidgetConfigToRegistry('button-widget', defaultConfig);
                                }
                            }

                            itemToAdd = {
                                id: uniqueInstanceId, 
                                type: 'widget',
                                widgetType: widgetTypeValue,
                                isReusable: isReusableComponent(componentType, componentId),
                                parentId: updatedSubmodel?.id,
                                content: widgetTypeValue === 'contentBlock' ? '' : undefined,
                                configuration: widgetConfiguration
                            };

                            newItems.push(itemToAdd);
                            newUsedComponents.push(componentToAdd);

                        } else if (componentType === 'submodel') {
                            contentType = 'subModel';
                            contentComponentId = componentId;
                            widgetType = undefined;

                            const dividers = container.usageInfo.usedComponents.filter(
                                (comp: any) => comp.originalComponentId && comp.originalComponentId.includes('divider')
                            );
                            newUsedComponents = [...dividers, componentToAdd];
                            
                            newItems = [];

                            itemToAdd = null;
                        }
                        
                        return {
                            ...container,
                            content: {
                                ...container.content,
                                type: contentType,
                                componentId: contentComponentId,
                                'widget-type': widgetType,
                                submodel: updatedSubmodel,
                                items: newItems
                            },
                            usageInfo: {
                                usedComponents: newUsedComponents
                            }
                        };
                    }

                    if (container.content.nestedContainers?.length) {
                        const { updatedContainers: newNestedContainers, addedSubmodel: nestedAddedSubmodel } = updateContainersRecursively(container.content.nestedContainers);
                        let newContent = { ...container.content, nestedContainers: newNestedContainers };
                        if (nestedAddedSubmodel) {
                            newContent.submodel = nestedAddedSubmodel;
                            addedSubmodel = nestedAddedSubmodel;
                        }
                        return { ...container, content: newContent };
                    }

                    return container;
                });
                return { updatedContainers, addedSubmodel };
            };

            const { updatedContainers } = updateContainersRecursively(pageContainers);

            return {
                ...prev,
                [activePageId]: updatedContainers
            };
        });
    };

    const addComponentsBatch = (containerId: string, components: { id: string, type: string }[]) => {
        if (!components.length) return;

        const activePageId = editorState.activePageId;
        if (!activePageId) return;

        setContainers((prevContainers: any) => {
            if (!prevContainers[activePageId]) {
                return prevContainers;
            }

            const tabContainers = [...prevContainers[activePageId]];

            const updatedContainers = tabContainers.map(container => {
                if (container.id === containerId) {
                    let updatedContainer = { ...container };
                    let newUsedComponents = [...container.usageInfo.usedComponents];
                    let newItems: any[] = [...(container.content.items || [])];
                    let updatedSubmodel = container.content.submodel;
                    
                    components.forEach(comp => {
                        const componentDef = getComponentDefinition(comp.id);

                        const uniqueInstanceId = generateInstanceId(comp.id);

                        const componentToAdd: any = {
                            id: uniqueInstanceId,
                            type: comp.type,
                            originalComponentId: comp.id,
                            isReusable: comp.type === 'field' ? true : isReusableComponent(comp.type, comp.id)
                        };

                        if (comp.type === 'submodel') {
                            updatedSubmodel = {
                                id: comp.id,
                                name: componentDef?.name || 'Unknown Submodel',
                                isTemporary: false,
                                configuration: getDefaultSubmodelConfiguration(componentDef?.name || 'Unknown Submodel')
                            };

                            const dividers = container.usageInfo.usedComponents.filter(
                                (comp: any) => comp.originalComponentId && comp.originalComponentId.includes('divider')
                            );
                            newUsedComponents = [...dividers, componentToAdd]; // ✅ REPLACES all components except dividers
                            
                            newItems = [];
                            
                        } else if (comp.type === 'field' || comp.type === 'category' || comp.type === 'connector') {
                            const fieldType = getFieldType(comp.id);
                            const fieldName = componentDef?.name || 'Field';

                            const defaultConfig = getDefaultFieldConfiguration(
                                comp.type === 'category' ? 'category' :
                                comp.type === 'connector' ? 'connector' :
                                fieldType,
                                fieldName
                            );

                            let itemToAdd: any = {
                                id: comp.id,
                                name: fieldName,
                                type: comp.type,
                                fieldType: comp.type === 'field' ? fieldType : undefined,
                                parentId: componentDef?.parentId || updatedSubmodel?.id,
                                isReusable: true,
                                dataConfiguration: componentDef?.dataConfiguration,
                                configuration: defaultConfig
                            };

                            if (comp.type === 'field' && fieldType === 'multichoice' && componentDef?.dataConfiguration?.choices) {
                                const firstTwoChoices = componentDef.dataConfiguration.choices.slice(0, 2);
                                itemToAdd.dataConfiguration = {
                                    choices: firstTwoChoices
                                };
                                itemToAdd.configuration = {
                                    ...getMultiChoiceConfig(componentDef),
                                    ...defaultConfig,
                                    choices: firstTwoChoices
                                };
                            }

                            if (comp.type === 'category' && componentDef?.dataConfiguration) {
                                const dataConfig = componentDef.dataConfiguration;

                                const wrapperId = dataConfig.id || comp.id;
                                const categoryId = dataConfig.categoryId || dataConfig.actualCategory?.id || null;
                                
                                const isEmpty = dataConfig.isEmpty || (!dataConfig.actualCategory && !categoryId);
                                
                                if (isEmpty) {
                                    console.warn(`Category "${dataConfig.wrapperName}" is missing actual category data. Wrapper ID: ${wrapperId}`);
                                }
                                
                                itemToAdd = {
                                    id: wrapperId,
                                    categoryId: categoryId,
                                    name: fieldName,
                                    type: comp.type,
                                    fieldType: undefined,
                                    parentId: componentDef?.parentId || updatedSubmodel?.id,
                                    isReusable: true,
                                    configuration: {
                                        ...getCategoryConfig(componentDef),
                                        ...defaultConfig,
                                        id: wrapperId,
                                        categoryId: categoryId,
                                        wrapperName: dataConfig.wrapperName,
                                        categoryName: dataConfig.actualCategoryName || dataConfig.categoryName,
                                        categoryType: dataConfig.categoryType,
                                        isEmpty: isEmpty
                                    }
                                };
                            }

                            newItems.push(itemToAdd);

                            if (componentDef?.parentId) {
                                componentToAdd.parentId = componentDef.parentId;
                            }

                            newUsedComponents.push(componentToAdd);
                            
                        } else if (comp.type === 'widget') {
                            const widgetType = getWidgetType(comp.id);
                            const widgetItem = {
                                id: comp.id,
                                type: 'widget',
                                widgetType: widgetType,
                                isReusable: isReusableComponent(comp.type, comp.id),
                                parentId: updatedSubmodel?.id
                            };

                            newItems.push(widgetItem);
                            newUsedComponents.push(componentToAdd);
                        }
                    });

                    updatedContainer = {
                        ...updatedContainer,
                        content: {
                            ...updatedContainer.content,
                            submodel: updatedSubmodel,
                            items: newItems
                        },
                        usageInfo: {
                            usedComponents: newUsedComponents
                        }
                    };

                    return updatedContainer;
                }
                
                return container;
            });

            return {
                ...prevContainers,
                [activePageId]: updatedContainers
            };
        });
    };

    const removeComponentFromContainer = (componentId: string, containerId: string) => {
        const activePageId = editorState.activePageId;
        if (!activePageId) return;

        releaseComponent(componentId, containerId);

        // Clear saved config for button widgets on removal so new instances get default config
        if (componentId.startsWith('button-widget-instance')) {
            clearWidgetConfigFromRegistry('button-widget');
        }

        setContainers(prev => {
            const pageContainers = prev[activePageId] || [];

            const hasSubmodelUsage = (container: ContainerType, submodelId: string): boolean => {
                if (container.content.items?.some(item => item.parentId === submodelId)) {
                    return true;
                }
                if (container.content.nestedContainers) {
                    for (const nested of container.content.nestedContainers) {
                        if (hasSubmodelUsage(nested, submodelId)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            const cleanupSubmodels = (containers: ContainerType[]): ContainerType[] => {
                const submodelsToRelease: { submodelId: string, containerId: string }[] = [];

                const processContainers = (containerList: ContainerType[]): ContainerType[] => {
                    return containerList.map(container => {
                        let cleanedNestedContainers = container.content.nestedContainers?.length
                            ? processContainers(container.content.nestedContainers)
                            : container.content.nestedContainers;

                        let updatedSubmodel = container.content.submodel;
                        if (updatedSubmodel) {
                            const submodelId = updatedSubmodel.id;
                            const tempContainer = {
                                ...container,
                                content: {
                                    ...container.content,
                                    nestedContainers: cleanedNestedContainers
                                }
                            };
                            if (!hasSubmodelUsage(tempContainer, submodelId)) {
                                updatedSubmodel = undefined;
                                submodelsToRelease.push({
                                    submodelId: submodelId,
                                    containerId: container.id
                                });
                            }
                        }

                        return {
                            ...container,
                            content: {
                                ...container.content,
                                submodel: updatedSubmodel,
                                nestedContainers: cleanedNestedContainers
                            }
                        };
                    });
                };

                const result = processContainers(containers);

                submodelsToRelease.forEach(({ submodelId, containerId }) => {
                    releaseComponent(submodelId, containerId);
                });

                return result;
            };

            const removeFromContainerRecursively = (containers: ContainerType[]): ContainerType[] => {
                return containers.map(container => {
                    if (container.id === containerId) {
                        const updatedItems = container.content.items?.filter(
                            item => item.id !== componentId
                        ) || [];

                        const updatedUsedComponents = container.usageInfo.usedComponents.filter(
                            used => used.id !== componentId && used.originalComponentId !== componentId
                        );

                        return {
                            ...container,
                            content: {
                                ...container.content,
                                items: updatedItems,
                            },
                            usageInfo: {
                                usedComponents: updatedUsedComponents
                            }
                        };
                    }

                    if (container.content.nestedContainers?.length) {
                        return {
                            ...container,
                            content: {
                                ...container.content,
                                nestedContainers: removeFromContainerRecursively(container.content.nestedContainers)
                            }
                        };
                    }

                    return container;
                });
            };

            const updatedPageContainers = removeFromContainerRecursively(pageContainers);
            const cleanedContainers = cleanupSubmodels(updatedPageContainers);

            return {
                ...prev,
                [activePageId]: cleanedContainers
            };
        });
    };

    const collectContainerComponents = (container: Container): Array<{id: string, type: string}> => {
        const components: Array<{id: string, type: string}> = [];
        
        if (container.content.submodel) {
            components.push({
                id: container.content.submodel.id,
                type: 'submodel'
            });
        }
        
        if (container.content.items) {
            container.content.items.forEach(item => {
                if (item.id && item.type) {
                    components.push({
                        id: item.id,
                        type: item.type
                    });
                }
            });
        }
        
        if (container.content.nestedContainers) {
            container.content.nestedContainers.forEach(nested => {
                components.push(...collectContainerComponents(nested));
            });
        }
        
        return components;
    };

    const deleteContainer = (containerId: string) => {
    
        if (!editorState.activePageId) {
            console.log("No active page, returning");
            return;
        }
        
        const currentPageId = editorState.activePageId;
        const currentContainers = containers[currentPageId] || [];
        
        const shouldClearSelection = editorState.selectedContainerId === containerId;
        
        let containerToDelete: Container | null = null;
        let componentsToRelease: Array<{id: string, type: string}> = [];
        
        const findContainer = (containerList: Container[], targetId: string): Container | null => {
            for (const container of containerList) {
                if (container.id === targetId) {
                    return container;
                }
                if (container.content.nestedContainers) {
                    const found = findContainer(container.content.nestedContainers, targetId);
                    if (found) return found;
                }
            }
            return null;
        };
        
        containerToDelete = findContainer(currentContainers, containerId);
        
        if (!containerToDelete) {
            console.warn(`Container ${containerId} not found`);
            return;
        }
        
        componentsToRelease = collectContainerComponents(containerToDelete);
        
        const processContainerDeletion = (containerList: Container[], targetId: string): Container[] => {
            return containerList.filter(container => {
                if (container.id === targetId) {
                    return false;
                }
                
                if (container.content.nestedContainers) {
                    container.content.nestedContainers = processContainerDeletion(
                        container.content.nestedContainers, 
                        targetId
                    );
                }
                
                return true;
            });
        };
        
        const updatedContainers = processContainerDeletion([...currentContainers], containerId);
        
        componentsToRelease.reverse().forEach(component => {
            const isUsedElsewhere = checkIfComponentUsedElsewhere(
                component.id, 
                component.type, 
                containerId, 
                updatedContainers
            );
            
            if (!isUsedElsewhere) {
                releaseComponent(component.id, containerId);
            }
        });
        
        const reorderedContainers = updatedContainers.map((container, index) => ({
            ...container,
            order: index
        }));
        
        setContainers(prev => ({
            ...prev,
            [currentPageId]: reorderedContainers
        }));
        
        if (shouldClearSelection) {
            setEditorState(prev => {
                const newState = {
                    ...prev,
                    selectedContainerId: null,
                    selectedFieldId: null,
                    selectedFieldType: null,
                    selectedFieldParentId: null,
                    selectedSubmodelId: null,
                    selectionPath: []
                };
                return newState;
            });
        }
        
        containerStore.clear();
    };

    const moveWidgetBetweenContainers = (
        widgetId: string,
        widgetType: string,
        fromContainerId: string,
        toContainerId: string,
        config?: any
    ) => {
        const activePageId = editorState.activePageId;
        if (!activePageId) return;

        setContainers(prevContainers => {
            const pageContainers = [...(prevContainers[activePageId] || [])];
            
            // Function to remove widget from container
            const removeFromContainer = (containers: Container[]): Container[] => {
                return containers.map(container => {
                    if (container.id === fromContainerId) {
                        const updatedItems = container.content.items?.filter(
                            item => !(item.id === widgetId && item.type === 'widget')
                        ) || [];
                        
                        const updatedUsedComponents = container.usageInfo.usedComponents.filter(
                            comp => comp.id !== widgetId
                        );

                        return {
                            ...container,
                            content: {
                                ...container.content,
                                items: updatedItems
                            },
                            usageInfo: {
                                usedComponents: updatedUsedComponents
                            }
                        };
                    }
                    
                    if (container.content.nestedContainers?.length) {
                        return {
                            ...container,
                            content: {
                                ...container.content,
                                nestedContainers: removeFromContainer(container.content.nestedContainers)
                            }
                        };
                    }
                    
                    return container;
                });
            };

            const addToContainer = (containers: Container[]): Container[] => {
                return containers.map(container => {
                    if (container.id === toContainerId) {
                        const widgetItem = {
                            id: widgetId,
                            type: 'widget' as const,
                            widgetType: widgetType,
                            isReusable: false,
                            configuration: config
                        };

                        const newItems = [...(container.content.items || []), widgetItem];
                        
                        const componentToAdd = {
                            id: widgetId,
                            type: 'widget' as const,
                            originalComponentId: widgetId,
                            isReusable: false
                        };

                        const newUsedComponents = [...container.usageInfo.usedComponents, componentToAdd];

                        return {
                            ...container,
                            content: {
                                ...container.content,
                                items: newItems
                            },
                            usageInfo: {
                                usedComponents: newUsedComponents
                            }
                        };
                    }
                    
                    if (container.content.nestedContainers?.length) {
                        return {
                            ...container,
                            content: {
                                ...container.content,
                                nestedContainers: addToContainer(container.content.nestedContainers)
                            }
                        };
                    }
                    
                    return container;
                });
            };

            // First remove from old container
            let updatedContainers = removeFromContainer(pageContainers);
            // Then add to new container
            updatedContainers = addToContainer(updatedContainers);

            return {
                ...prevContainers,
                [activePageId]: updatedContainers
            };
        });

        const location = {
            containerId: toContainerId,
            pageId: editorState.activePageId!,
            groupId: editorState.activeGroupId!
        };
        moveWidget(widgetId, fromContainerId, toContainerId, location);
    };

    const saveWidgetConfig = (widgetId: string, config: any) => {
        saveWidgetConfigToRegistry(widgetId, config);
    };

    const getWidgetConfigFromRegistry = (widgetId: string): any => {
        return getWidgetConfigFromRegistryMethod(widgetId);
    };

    const checkIfComponentUsedElsewhere = (
        componentId: string, 
        componentType: string, 
        excludeContainerId: string,
        containers: Container[]
    ): boolean => {
        for (const container of containers) {
            if (container.id !== excludeContainerId) {
                if (componentType === 'submodel' && container.content.submodel?.id === componentId) {
                    return true;
                }
                
                if (container.content.items) {
                    const found = container.content.items.some(item => 
                        item.id === componentId && item.type === componentType
                    );
                    if (found) return true;
                }
                
                if (container.content.nestedContainers) {
                    const foundInNested = checkIfComponentUsedElsewhere(
                        componentId, 
                        componentType, 
                        excludeContainerId,
                        container.content.nestedContainers
                    );
                    if (foundInNested) return true;
                }
            }
        }
        
        return false;
    };

    return (
        <EditorContext.Provider
            value={{
                editorState,
                setEditorState,
                layoutState,
                setLayoutState,
                themeState,
                setThemeState,
                cssState,
                setCssState,
                groups,
                setGroups,
                containers,
                setContainers,
                addComponentToContainer,
                removeComponentFromContainer,
                addComponentsBatch,
                uiModel,
                setUiModel,
                customCSS,
                setCustomCSS,
                selectedFieldId,
                selectedFieldType,
                selectedFieldParentId,
                selectedWidgetId,
                selectedWidgetType,
                selectField,
                selectSubmodel,
                selectWidget,
                selectContainer,
                clearAllSelections,
                setSelectionPath,
                clearSelectionPath,
                deleteContainer,
                updateWidgetContent,
                theme,
                setTheme,
                moveWidgetBetweenContainers,
                saveWidgetConfig,
                getWidgetConfig: getWidgetConfigFromRegistry,
            }}
        >
            {children}
        </EditorContext.Provider>
    );
};

export const useEditor = () => {
    const context = useContext(EditorContext);
    if (context === undefined) {
        throw new Error('useEditor must be used within an EditorProvider');
    }
    return context;
};